import type { BookingQuestion } from '@shared/core/appointment-types'
import type { BookingAnswerValue } from '@shared/core/bookings'
import { useCallback, useMemo, useState } from 'react'
import type { BookingAnswerMap, PinnedBookingForm } from 'src/modules/booking/types/booking-form.types'
import { buildBookingFormPayload, getBookingQuestionError } from './booking-details-step.domain'
import type { BookingQuestionErrors } from './booking-details-step.interface'

const NO_ANSWERS: BookingAnswerMap = {}

interface UseBookingQuestionsOptions {
  form?: PinnedBookingForm
  answers?: BookingAnswerMap
  onAnswerChange: (questionId: string, value: BookingAnswerValue) => void
}

/** Answers live in the booking wrapper (design D35); this hook only owns their validation errors. */
export function useBookingQuestions({ form, answers = NO_ANSWERS, onAnswerChange }: UseBookingQuestionsOptions) {
  const questions = useMemo(() => (form && form.revision >= 1 ? form.questions : []), [form])
  const [errors, setErrors] = useState<BookingQuestionErrors>({})

  const handleChange = useCallback(
    (question: BookingQuestion, value: BookingAnswerValue) => {
      onAnswerChange(question.id, value)
      setErrors((prev) => ({ ...prev, [question.id]: null }))
    },
    [onAnswerChange],
  )

  const handleBlur = useCallback(
    (question: BookingQuestion) => {
      setErrors((prev) => ({ ...prev, [question.id]: getBookingQuestionError(question, answers[question.id]) }))
    },
    [answers],
  )

  const submission = useMemo(() => buildBookingFormPayload(form, answers), [form, answers])

  const validate = useCallback((): boolean => {
    const next = Object.fromEntries(questions.map((q) => [q.id, getBookingQuestionError(q, answers[q.id])]))
    setErrors(next)
    return Object.values(next).every((error) => !error)
  }, [questions, answers])

  return {
    questions,
    answers,
    errors,
    hasErrors: Object.values(errors).some(Boolean),
    submission,
    handleChange,
    handleBlur,
    validate,
  }
}
